import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Tv, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from './SiteHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const cur = (c) => (c === 'USD' ? '$' : '£');
// "Free to you" = you already subscribe (signed in); "Free to all" = genuinely
// free; otherwise the monthly price.
const priceLabel = (s) => (s.free_to_you ? 'Free to you' : s.is_free ? 'Free to all' : `${cur(s.currency)}${s.monthly_price}/mo`);
const priceClass = (s) => (s.free_to_you || s.is_free ? 'text-green-600 font-medium' : 'text-gray-600');

// Sports section (Phase A): which sports services carry what, where to watch
// online, and the cheapest combination for the competitions you follow. Covers
// mainstream and extreme sports across UK / US / World. Coverage is maintained
// config (no live sports feed yet).
export default function Sports() {
  const { token } = useAuth();
  const [region, setRegion] = useState('UK');
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [result, setResult] = useState(null);

  const authHeaders = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  useEffect(() => {
    setSelected(new Set());
    setResult(null);
    setData(null);
    fetch(`${API_BASE}/api/sports/services?region=${region}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, [region, authHeaders]);

  const optimise = useCallback(async (comps) => {
    if (comps.length === 0) { setResult(null); return; }
    try {
      const r = await fetch(`${API_BASE}/api/sports/optimise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ region, competitions: comps }),
      });
      if (r.ok) setResult(await r.json());
    } catch { /* ignore */ }
  }, [region, authHeaders]);

  const toggle = (comp) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(comp)) next.delete(comp); else next.add(comp);
      optimise([...next]);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Trophy className="w-6 h-6 text-videbits-600" /> Sports
          <span className="text-xs font-medium text-videbits-700 bg-videbits-50 border border-videbits-200 rounded-full px-2.5 py-0.5">✨ New</span>
        </h1>
        <p className="text-sm text-gray-500 mb-4">Pick the competitions you follow and we'll work out the cheapest services you need — and where to watch online.</p>

        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 mb-6">
          {(data?.regions || ['UK', 'US', 'World']).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setRegion(key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                region === key ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
              }`}
            >
              {key}
            </button>
          ))}
        </div>

        {!data ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : (
          <>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Competitions you follow</h2>
            <div className="flex flex-wrap gap-2 mb-6">
              {data.competitions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggle(c)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selected.has(c) ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            {result && (
              <div className="bg-white border border-videbits-200 rounded-xl p-4 mb-8">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">You need</h3>
                {result.recommended_services.length === 0 ? (
                  <p className="text-sm text-gray-500">No service covers your selection.</p>
                ) : (
                  <>
                    <ul className="text-sm text-gray-800 mb-2 space-y-1">
                      {result.recommended_services.map((s) => (
                        <li key={s.key} className="flex justify-between items-center">
                          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-videbits-700 hover:underline inline-flex items-center gap-1">
                            {s.name} <ExternalLink className="w-3 h-3" />
                          </a>
                          <span className="text-gray-500">{priceLabel(s)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-between text-sm font-semibold border-t border-gray-100 pt-2">
                      <span>Total</span>
                      <span>{cur(result.currency)}{result.monthly_cost.toFixed(2)}/mo</span>
                    </div>
                  </>
                )}
                {result.uncovered.length > 0 && (
                  <p className="text-xs text-amber-700 mt-2">Not available on tracked services: {result.uncovered.join(', ')}</p>
                )}
              </div>
            )}

            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Services — where to watch</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.services.map((s) => (
                <div key={s.key} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="font-medium text-gray-900 hover:text-videbits-700 flex items-center gap-1.5">
                      <Tv className="w-4 h-4 text-gray-400" /> {s.name} <ExternalLink className="w-3 h-3 text-gray-400" />
                    </a>
                    <span className={`text-sm ${priceClass(s)}`}>{priceLabel(s)}</span>
                  </div>
                  <p className="text-xs text-gray-500">{s.competitions.join(' · ')}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
